import { useState, useRef } from "react";
import { eur } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  GoogleLogo, UploadSimple, ArrowSquareOut, CheckCircle, Info, ArrowClockwise,
} from "@phosphor-icons/react";

const TAKEOUT_URL = "https://takeout.google.com/settings/takeout/custom/google_pay";

export default function GooglePaySyncDialog({ open, onOpenChange, onPreview, onCommit }) {
  const [step, setStep] = useState("intro"); // intro | preview | done
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [insertedCount, setInsertedCount] = useState(0);
  const fileRef = useRef(null);

  const reset = () => { setStep("intro"); setPreview(null); setInsertedCount(0); };

  const close = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(reset, 200);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const result = await onPreview(file);
      if (!result.rows.length) {
        toast.error("No transactions found in that file. Make sure it's a Google Pay export (CSV or HTML).");
      } else {
        setPreview(result);
        setStep("preview");
      }
    } catch (err) {
      toast.error("Could not read that file.");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    if (!preview) return;
    setLoading(true);
    try {
      const newRows = preview.rows.filter((r) => !r.duplicate);
      const result = await onCommit(newRows);
      setInsertedCount(result.inserted);
      setStep("done");
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="gpay-sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
              <GoogleLogo size={16} weight="bold" className="text-black" />
            </span>
            Sync Google Pay
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Import your Google Pay transactions into FinFlo.
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-xs text-muted-foreground border border-border rounded-md p-3">
              <Info size={15} className="text-forecast shrink-0 mt-0.5" />
              <p>
                Google doesn't offer an API for apps to read your Google Pay history, so sync uses your
                official Google export. It takes about a minute and only new transactions are added each time.
              </p>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="w-5 h-5 rounded-full bg-secondary text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                <div>
                  Open your Google Pay export and download it (choose CSV or HTML).
                  <a href={TAKEOUT_URL} target="_blank" rel="noopener noreferrer"
                     data-testid="gpay-takeout-link"
                     className="text-forecast hover:underline inline-flex items-center gap-1 mt-1">
                    Open Google Takeout <ArrowSquareOut size={13} />
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-5 h-5 rounded-full bg-secondary text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                <div>Upload the file below — I'll auto-categorize and remove duplicates before importing.</div>
              </li>
            </ol>
            <input ref={fileRef} type="file" accept=".csv,.html,.htm" onChange={handleFile}
                   className="hidden" data-testid="gpay-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={loading}
                    className="w-full h-11 bg-income text-black hover:bg-income/90 font-semibold gap-2"
                    data-testid="gpay-upload-btn">
              <UploadSimple size={17} weight="bold" /> {loading ? "Reading…" : "Upload Google Pay export"}
            </Button>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Found" value={preview.summary.total} />
              <Stat label="New" value={preview.summary.new} accent="text-income" />
              <Stat label="Duplicates" value={preview.summary.duplicates} accent="text-muted-foreground" />
            </div>
            <div className="border border-border rounded-md max-h-64 overflow-auto" data-testid="gpay-preview-list">
              <table className="w-full text-sm">
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className={`border-b border-border/60 ${r.duplicate ? "opacity-40" : ""}`}>
                      <td className="px-3 py-2 text-muted-foreground tabular-nums whitespace-nowrap">{r.date}</td>
                      <td className="px-3 py-2 max-w-[160px] truncate">{r.description}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="border-border font-normal text-[10px]">{r.category}</Badge></td>
                      <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.type === "income" ? "text-income" : "text-expense"}`}>
                        {r.type === "income" ? "+" : "−"}{eur(r.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.summary.new === 0 && (
              <p className="text-xs text-muted-foreground text-center">Everything here is already imported — nothing new to add.</p>
            )}
          </div>
        )}

        {step === "done" && (
          <div className="py-6 text-center space-y-3">
            <CheckCircle size={48} weight="fill" className="text-income mx-auto" />
            <p className="font-heading font-bold text-lg">Sync complete</p>
            <p className="text-sm text-muted-foreground">
              Added {insertedCount} new transaction{insertedCount === 1 ? "" : "s"} from Google Pay.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === "preview" && (
            <>
              <Button variant="ghost" onClick={reset} data-testid="gpay-back-btn">Choose another file</Button>
              <Button onClick={commit} disabled={loading || preview.summary.new === 0}
                      className="bg-income text-black hover:bg-income/90 font-semibold gap-1.5"
                      data-testid="gpay-import-btn">
                <ArrowClockwise size={15} /> {loading ? "Importing…" : `Import ${preview.summary.new} new`}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button onClick={() => close(false)} className="bg-income text-black hover:bg-income/90 font-semibold" data-testid="gpay-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="border border-border rounded-md p-3 text-center">
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`font-heading font-bold text-xl mt-1 tabular-nums ${accent || ""}`}>{value}</p>
  </div>
);
